import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";

import {
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { useTranslation } from "react-i18next";

const CategoryDistributionChart = ({ data }) => {
  const { t } = useTranslation();
  const chartData =
    data?.map((item, index) => ({
      name: item.labels?.[0] || t("common.notAvailableShort", "N/A"),
      value: item.data?.[0] || 0,
      fill: item.colors?.[0] || `hsl(${index * 45}, 70%, 50%)`,
    })) || [];

  const total = chartData.reduce((sum, item) => sum + item.value, 0);

  const chartDataWithPercentage = chartData.map((item) => ({
    ...item,
    percentage: total > 0 ? ((item.value / total) * 100).toFixed(1) : 0,
  }));

  const chartConfig = chartDataWithPercentage.reduce((acc, item) => {
    acc[item.name] = {
      label: item.name,
      color: item.fill,
    };
    return acc;
  }, {});

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="rounded-lg border bg-white p-3 shadow-md">
          <p className="mb-1 font-semibold text-[#111729]">{data.name}</p>
          <div className="flex items-center justify-between gap-4">
            <span className="text-sm text-[#677389]">
              {t("extendedWarranty.reports.categoryDistribution.value", "Değer")}
              :
            </span>
            <span className="font-medium text-[#111729]">{data.value}</span>
          </div>
          <div className="flex items-center justify-between gap-4">
            <span className="text-sm text-[#677389]">
              {t("extendedWarranty.reports.categoryDistribution.rate", "Oran")}:
            </span>
            <span className="font-medium text-[#111729]">
              %{data.percentage}
            </span>
          </div>
        </div>
      );
    }
    return null;
  };

  const CustomLegend = ({ payload }) => {
    return (
      <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
        {payload?.map((entry, index) => (
          <div key={index} className="flex items-center gap-2">
            <div
              className="h-3 w-3 flex-shrink-0 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span
              className="truncate text-sm text-[#677389]"
              title={entry.value}
            >
              {entry.value} (%
              {
                chartDataWithPercentage.find(
                  (item) => item.name === entry.value,
                )?.percentage
              }
              )
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="mb-6 rounded-lg border bg-white p-6 lg:mb-0">
      <h3 className="mb-4 text-lg font-medium text-[#111729]">
        {t(
          "extendedWarranty.reports.categoryDistribution.title",
          "Kategori Bazlı Satış Dağılımı",
        )}
      </h3>
      <p className="mb-4 text-sm text-[#677389]">
        {t(
          "extendedWarranty.reports.categoryDistribution.description",
          "Ürün kategorilerine göre satış yüzdeleri",
        )}
      </p>
      <div className="h-80">
        {data && data.length > 0 ? (
          <ChartContainer config={chartConfig} className="h-full w-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartDataWithPercentage}
                  cx="50%"
                  cy="40%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {chartDataWithPercentage.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend content={<CustomLegend />} />
              </PieChart>
            </ResponsiveContainer>
          </ChartContainer>
        ) : (
          <div className="flex h-full items-center justify-center text-[#677389]">
            {t(
              "extendedWarranty.reports.common.loadingData",
              "Veri yükleniyor...",
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryDistributionChart;
